// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api-client.js';
import { useLocale } from '../hooks/useLocale.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useAuth } from '../contexts/AuthContext.js';
import { extractErrorMessage, extractFieldErrors } from '../utils/api-helpers.js';

export { PASSWORD_CHANGE_REQUIRED_ROUTE } from '../utils/password-change-required.js';

type Field = 'current_password' | 'new_password' | 'confirm_password';

/**
 * Sprint 63.29.0 (#541, owner ruling Q4 (a)) — the change-password step a
 * session reaches when an administrator requires a new password.
 *
 * Outside `ProtectedRoute`, like the MFA interstitials: the session is real and
 * the server admits it to exactly three paths (`POST /auth/change-password`,
 * `GET /auth/me`, `POST /auth/logout`), and this page calls only those.
 *
 * A successful change ends every session of the user (the server revokes them
 * all), so the step ends on a sign-in, not on the product.
 */
export function PasswordChangeRequiredPage() {
  const { t } = useLocale();
  const copy = t.login;
  usePageTitle(copy.password_change_required_title);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [values, setValues] = useState<Record<Field, string>>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // WCAG 2.4.3 — the step announces itself: focus starts on its heading.
  useEffect(() => {
    headingRef.current?.focus();
  }, [done]);

  function set(field: Field, value: string) {
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    const next: Partial<Record<Field, string>> = {};
    for (const field of ['current_password', 'new_password', 'confirm_password'] as const) {
      if (!values[field]) next[field] = copy.password_change_field_required;
    }
    if (!next.confirm_password && values.new_password !== values.confirm_password) {
      next.confirm_password = copy.password_change_mismatch;
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setSaving(true);
    try {
      await apiClient.changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      setDone(true);
      // Every session of this user has just ended on the server; clear the
      // interface's copy of it too.
      logout();
    } catch (err: unknown) {
      const fieldErrors = extractFieldErrors(err);
      const mapped: Partial<Record<Field, string>> = {};
      if (fieldErrors.current_password) mapped.current_password = fieldErrors.current_password;
      if (fieldErrors.new_password) mapped.new_password = fieldErrors.new_password;
      setErrors(mapped);
      if (Object.keys(mapped).length === 0) {
        setFormError(extractErrorMessage(err, copy.password_change_failed));
      }
    } finally {
      setSaving(false);
    }
  }

  if (done) {
    return (
      <div className="dm-page">
        <div className="dm-header">
          <h1 ref={headingRef} tabIndex={-1}>
            {copy.password_change_required_title}
          </h1>
        </div>
        <p className="dm-alert dm-alert--success" role="status">
          {copy.password_change_done}
        </p>
        <button type="button" className="dm-btn dm-btn-primary" onClick={() => navigate('/login')}>
          {copy.sign_in}
        </button>
      </div>
    );
  }

  const fields: Array<{ field: Field; label: string; hint?: string; autoComplete: string }> = [
    {
      field: 'current_password',
      label: copy.password_change_current,
      hint: copy.password_change_current_hint,
      autoComplete: 'current-password',
    },
    {
      field: 'new_password',
      label: copy.password_change_new,
      hint: copy.password_change_new_hint,
      autoComplete: 'new-password',
    },
    {
      field: 'confirm_password',
      label: copy.password_change_confirm,
      autoComplete: 'new-password',
    },
  ];

  return (
    <div className="dm-page">
      <div className="dm-header">
        <h1 ref={headingRef} tabIndex={-1}>
          {copy.password_change_required_title}
        </h1>
        <p>{copy.password_change_required_intro}</p>
      </div>

      {formError && (
        <p className="dm-alert dm-alert--danger" role="alert">
          {formError}
        </p>
      )}

      <form className="dm-form" onSubmit={handleSubmit} noValidate>
        {fields.map(({ field, label, hint, autoComplete }) => {
          const inputId = `pcr-${field}`;
          const hintId = hint ? `${inputId}-hint` : undefined;
          const errorId = errors[field] ? `${inputId}-error` : undefined;
          const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
          return (
            <div className="dm-form-field" key={field}>
              <label htmlFor={inputId}>{label} *</label>
              <input
                id={inputId}
                type="password"
                autoComplete={autoComplete}
                value={values[field]}
                onChange={(e) => set(field, e.target.value)}
                required
                aria-required="true"
                aria-invalid={errors[field] ? true : undefined}
                aria-describedby={describedBy}
                disabled={saving}
              />
              {hint && (
                <p id={hintId} className="dm-form-hint">
                  {hint}
                </p>
              )}
              {errors[field] && (
                <p id={errorId} className="dm-field-error" role="alert">
                  {errors[field]}
                </p>
              )}
            </div>
          );
        })}
        <button
          type="submit"
          className="dm-btn dm-btn-primary"
          disabled={saving}
          aria-busy={saving}
        >
          {saving ? t.common.saving : copy.password_change_submit}
        </button>
      </form>

      <button
        type="button"
        className="dm-btn dm-btn-secondary"
        onClick={() => {
          logout();
          navigate('/login');
        }}
      >
        {copy.password_change_sign_out}
      </button>
    </div>
  );
}
